import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import {
  getRequests,
  checkOutBox,
  returnBox,
  getLocations,
  type PhysicalRecordsRequest,
  type RecordLocation,
} from "@/api/physicalRecordsApi";

export default function AdminPhysicalRecordsRequests() {
  const [requests, setRequests] = useState<PhysicalRecordsRequest[]>([]);
  const [locations, setLocations] = useState<RecordLocation[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [locationChoice, setLocationChoice] = useState<Record<number, number>>({});

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const [r, l] = await Promise.all([getRequests(statusFilter || undefined), getLocations()]);
      setRequests(r);
      setLocations(l);
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? e?.message ?? "Failed to load requests.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  async function onCheckOut(requestId: number) {
    const toLocationId = locationChoice[requestId];
    if (!toLocationId) {
      setErr("Choose a location to check the box out to.");
      return;
    }
    try {
      setErr(null);
      await checkOutBox(requestId, toLocationId);
      await load();
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? e?.message ?? "Failed to check out box.");
    }
  }

  async function onReturn(requestId: number) {
    const returnLocationId = locationChoice[requestId];
    if (!returnLocationId) {
      setErr("Choose the location the box is being returned to.");
      return;
    }
    try {
      setErr(null);
      await returnBox(requestId, returnLocationId);
      await load();
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? e?.message ?? "Failed to return box.");
    }
  }

  return (
    <div className="container py-4">
      <div className="mb-3">
        <h1 className="h4 mb-1">Physical Records — Requests &amp; Transfers</h1>
        <div className="text-muted">
          Check boxes out to requesting users, and record returns. Audit trail: Requested → Borrowed → Returned.
        </div>
      </div>

      {err && <div className="alert alert-danger">{err}</div>}

      <div className="mb-3">
        <select className="form-select" style={{ maxWidth: 240 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="Requested">Requested</option>
          <option value="Borrowed">Borrowed</option>
          <option value="Returned">Returned</option>
        </select>
      </div>

      {loading ? (
        <div>Loading...</div>
      ) : (
        <table className="table table-sm">
          <thead>
            <tr>
              <th>Box</th>
              <th>Requested By</th>
              <th>Requested Date</th>
              <th>Status</th>
              <th>Notes</th>
              <th style={{ minWidth: 260 }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((r) => (
              <tr key={r.requestId}>
                <td>
                  <Link to="/physical-records">
                    {r.boxNumber} — {r.boxName}
                  </Link>
                </td>
                <td>{r.requestedBy}</td>
                <td>{new Date(r.requestedDt).toLocaleString()}</td>
                <td>{r.status}</td>
                <td>{r.notes ?? "—"}</td>
                <td>
                  {(r.status === "Requested" || r.status === "Borrowed") && (
                    <div className="d-flex gap-2">
                      <select
                        className="form-select form-select-sm"
                        value={locationChoice[r.requestId] ?? ""}
                        onChange={(e) => setLocationChoice({ ...locationChoice, [r.requestId]: Number(e.target.value) })}
                      >
                        <option value="">Location...</option>
                        {locations.map((l) => (
                          <option key={l.locationId} value={l.locationId}>
                            {l.name}
                          </option>
                        ))}
                      </select>
                      {r.status === "Requested" ? (
                        <button className="btn btn-sm btn-primary text-nowrap" onClick={() => onCheckOut(r.requestId)}>
                          Check Out
                        </button>
                      ) : (
                        <button className="btn btn-sm btn-outline-primary text-nowrap" onClick={() => onReturn(r.requestId)}>
                          Return
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {requests.length === 0 && (
              <tr>
                <td colSpan={6} className="text-muted text-center">
                  No requests found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
